import { DeviceCounter } from '@shared/models/device-info';
import { DeviceCounterEntity, DeviceCounterEntityType } from './device-counter.models';

/**
 * Device counter business rules, expressed as pure functions.
 *
 * These are the rules the Native Service (`edara-electron`
 * `services/DeviceService.js`) applies today, ported so the application owns
 * them: in Phase 1 they describe the state the Desktop produces, in Phase 2
 * the Firestore store enforces them directly. Inputs are never mutated.
 */

export type DeviceCounterStateErrorCode = 'CounterAtZero';

/** Raised when a transition is refused; mirrors the Desktop's 500 `{ message }` answer. */
export class DeviceCounterStateError extends Error {
  readonly status = 500;
  readonly error: { message: string; code: DeviceCounterStateErrorCode };

  constructor(readonly code: DeviceCounterStateErrorCode, message: string) {
    super(message);
    this.name = 'DeviceCounterStateError';
    this.error = { message, code };
  }
}

/** A counter the Desktop creates on first use: zero, "reset" and created now. */
export function createCounter(
  cashRegisterId: string,
  entityType: DeviceCounterEntityType,
  now: Date = new Date()
): DeviceCounterEntity {
  return { cashRegisterId, entityType, current: 0, lastResetAt: now, createdAt: now };
}

/** `PUT /counters/increment`: creates the counter when missing, then adds one. */
export function applyIncrement(
  existing: DeviceCounterEntity | undefined,
  cashRegisterId: string,
  entityType: DeviceCounterEntityType,
  now: Date = new Date()
): DeviceCounterEntity {
  const counter = existing ?? createCounter(cashRegisterId, entityType, now);
  return { ...counter, current: (counter.current || 0) + 1 };
}

/** `PUT /counters/decrement`: refuses at zero, otherwise subtracts one. Ported for completeness (unused by the POS). */
export function applyDecrement(
  existing: DeviceCounterEntity | undefined,
  cashRegisterId: string,
  entityType: DeviceCounterEntityType,
  now: Date = new Date()
): DeviceCounterEntity {
  const counter = existing ?? createCounter(cashRegisterId, entityType, now);
  if (!(counter.current > 0)) {
    throw new DeviceCounterStateError('CounterAtZero', `Counter for ${entityType} is already at 0 and cannot be decremented.`);
  }
  return { ...counter, current: counter.current - 1 };
}

/** `PUT /counters/reset`: creates the counter when missing, zeroes it and stamps the reset. */
export function applyReset(
  existing: DeviceCounterEntity | undefined,
  cashRegisterId: string,
  entityType: DeviceCounterEntityType,
  now: Date = new Date()
): DeviceCounterEntity {
  const counter = existing ?? createCounter(cashRegisterId, entityType, now);
  return { ...counter, current: 0, lastResetAt: now };
}

/** `PUT /counters/set`: creates the counter when missing, sets `parseInt(value) || 0` and stamps the reset. */
export function applySet(
  existing: DeviceCounterEntity | undefined,
  cashRegisterId: string,
  entityType: DeviceCounterEntityType,
  value: number | string,
  now: Date = new Date()
): DeviceCounterEntity {
  const counter = existing ?? createCounter(cashRegisterId, entityType, now);
  return { ...counter, current: parseInt(String(value), 10) || 0, lastResetAt: now };
}

/** The wire shape the Native Service answers with and every consumer reads. */
export function toWireCounter(entity: DeviceCounterEntity): DeviceCounter {
  return {
    cash_register_id: entity.cashRegisterId,
    entity_type: entity.entityType,
    current: entity.current,
    Created_at: entity.createdAt,
    last_reset_at: entity.lastResetAt
  };
}
